import { FC, useMemo, useState } from 'react';
import { MessageSquareText } from 'lucide-react';
import type { UserCourse } from '@teachur/http-client';
import { sortCourseNotesByNewest } from '../../utils/courseNoteHelpers';

interface CourseNotesProps {
  notes: UserCourse['notes'];
}

export const CourseNotes: FC<CourseNotesProps> = ({ notes = [] }) => {
  const [showAllNotes, setShowAllNotes] = useState(false);

  const sortedNotes = useMemo(() => sortCourseNotesByNewest(notes), [notes]);

  const hasMultipleNotes = sortedNotes.length > 1;
  const visibleNotes = showAllNotes ? sortedNotes : sortedNotes.slice(0, 1);

  return (
    <div className="flex w-full flex-col gap-3 rounded-xl border border-(--primary-O500) bg-(--warning-W100) p-5">
      <div className="flex w-full items-start gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-full border border-(--primary-O700) bg-(--primary-O100)">
          <MessageSquareText className="size-4 text-(--primary-O800)" />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <h5 className="[font-size:var(--fs-h5)] [font-family:var(--font-montserrat)]! m-0! font-semibold! text-(--neutral-black-NB800)!">
            Course Notes
          </h5>

          {sortedNotes.length === 0 ? (
            <p className="m-0! text-sm font-medium text-(--neutral-black-NB700)">
              No notes for this course.
            </p>
          ) : (
            <div className="flex w-full flex-col gap-3">
              {visibleNotes.map((note) => (
                <div
                  key={note.id}
                  className="flex w-full flex-col gap-3 rounded-xl border border-(--primary-O300) bg-(--neutral-white-NW1000) p-4 shadow-sm"
                >
                  <div>
                    <span className="[font-size:var(--fs-p2)] m-0! font-semibold text-(--neutral-black-NB800)">
                      {note.approved ? 'Approved' : 'Not Approved'}
                    </span>

                    {note.approver && (
                      <p className="[font-size:var(--fs-p4)] m-0! font-semibold! text-(--neutral-black-NB600)">
                        Approver: {note.approver.firstName} {note.approver.lastName}
                      </p>
                    )}
                  </div>

                  <p className="[font-size:var(--fs-p4)] m-0! font-medium text-(--neutral-black-NB600)">
                    {note.text}
                  </p>
                </div>
              ))}

              {hasMultipleNotes && (
                <button
                  type="button"
                  onClick={() => setShowAllNotes((prev) => !prev)}
                  className="w-fit cursor-pointer border-0 bg-transparent p-0 [font-size:var(--fs-p4)]! font-bold! text-(--primary-O900)!"
                >
                  {showAllNotes ? 'Show less' : 'Show all notes'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
